/**
 * useWebSocket — Real-time WebSocket hook for Clobi.
 * Connects to ws://{tenant}.localhost:8000/ws/notifications/?token={jwt}
 * Reconnects with exponential backoff. Invalidates TanStack Query caches
 * on incoming events.
 */
import { useEffect, useRef, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/store/authStore'
import { redirectToLogin, refreshAccessToken } from '@/lib/apiClient'

type WSEventType =
  | 'notification_new'
  | 'document_status_changed'
  | 'payment_confirmed'
  | 'voucher_status_changed'
  | 'pong'

interface WSMessage {
  type: WSEventType
  data?: Record<string, unknown>
}

// Query keys to invalidate per event type
const INVALIDATION_MAP: Record<string, string[][]> = {
  notification_new: [['notifications']],
  document_status_changed: [
    ['sales', 'documents'],
    ['sales', 'dashboard'],
  ],
  payment_confirmed: [
    ['sales', 'payments'],
    ['sales', 'collections'],
    ['sales', 'documents'],
    ['sales', 'dashboard'],
  ],
  voucher_status_changed: [['accounting', 'vouchers']],
}

const PING_INTERVAL = 30_000
const MAX_RECONNECT_DELAY = 30_000
// Cierre del servidor cuando otra sesión tomó la licencia (sesión única).
// Debe coincidir con WS_CLOSE_SESSION_SUPERSEDED en apps/users/session.py.
const WS_CLOSE_SESSION_SUPERSEDED = 4001

export function useWebSocket() {
  const { accessToken } = useAuthStore()
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)
  // Ref a la última versión de connect, para que scheduleReconnect la invoque
  // sin crear una dependencia circular entre ambos useCallback.
  const connectRef = useRef<() => void>(() => {})

  const cleanup = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current)
      pingTimerRef.current = null
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.onclose = null // prevent reconnect on intentional close
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return
    const attempt = reconnectAttemptRef.current
    const delay = Math.min(1000 * Math.pow(2, attempt), MAX_RECONNECT_DELAY)
    reconnectAttemptRef.current = attempt + 1
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) void connectRef.current()
    }, delay)
  }, [])

  const connect = useCallback(async () => {
    if (!mountedRef.current) return

    // Asegura un JWT vigente ANTES de abrir el WebSocket: reconectar con un
    // token expirado produce un handshake 403 en bucle (el backend cierra la
    // conexión sin aceptar el upgrade). Si el usuario dejó la pestaña inactiva
    // más de la vida del access token, lo refrescamos vía cookie httpOnly.
    let token = useAuthStore.getState().accessToken
    if (!token || useAuthStore.getState().isTokenExpired()) {
      token = await refreshAccessToken()
    }
    if (!mountedRef.current) return
    if (!token) {
      // Refresh falló (p.ej. tras logout): no abrimos el WS con un token muerto.
      // Reintentamos con backoff por si el usuario vuelve a autenticarse.
      scheduleReconnect()
      return
    }

    const { hostname, protocol } = window.location
    const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:'

    // In dev, frontend is on :5173 but WS server is on :8000
    let wsHost = hostname
    if (import.meta.env.DEV) {
      wsHost = `${hostname}:8000`
    }

    const url = `${wsProtocol}//${wsHost}/ws/notifications/?token=${token}`

    try {
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        reconnectAttemptRef.current = 0

        // Start ping keepalive
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }))
          }
        }, PING_INTERVAL)
      }

      ws.onmessage = (event) => {
        try {
          const msg: WSMessage = JSON.parse(event.data)
          if (msg.type === 'pong') return

          // Invalidate relevant queries
          const keys = INVALIDATION_MAP[msg.type]
          if (keys) {
            keys.forEach((queryKey) => {
              queryClient.invalidateQueries({ queryKey })
            })
          }
        } catch {
          // ignore malformed messages
        }
      }

      ws.onclose = (event) => {
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current)
          pingTimerRef.current = null
        }
        wsRef.current = null

        // Sesión única: el servidor nos expulsó porque el usuario inició sesión
        // en otro dispositivo. Reconectar es inútil (el handshake rechazará el
        // mismo sid) → cerrar sesión acá mismo, sin esperar a la próxima llamada
        // HTTP para que el usuario entienda qué pasó.
        if (event.code === WS_CLOSE_SESSION_SUPERSEDED) {
          useAuthStore.getState().logout()
          redirectToLogin('session_superseded')
          return
        }

        // Reconexión con backoff exponencial. connect() refrescará el token si
        // hizo falta, de modo que un cierre por token expirado se recupera solo.
        scheduleReconnect()
      }

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnect
      }
    } catch {
      // connection failed: programa reconexión con backoff
      scheduleReconnect()
    }
  }, [queryClient, scheduleReconnect])

  // Mantiene connectRef apuntando a la versión vigente de connect.
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  useEffect(() => {
    mountedRef.current = true
    void connect()

    return () => {
      mountedRef.current = false
      cleanup()
    }
  }, [accessToken, connect, cleanup])
}
